import React, { useState } from 'react';
import { 
  MapPinIcon, 
  EnvelopeIcon,
  ClockIcon,
  BuildingOfficeIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { message } from 'antd';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: MapPinIcon,
      title: '地址',
      content: '云南省昆明市呈贡区云南大学呈贡校区',
      detail: '材料与能源学院 物理与天文学院楼负一层'
    },
    {
      icon: EnvelopeIcon,
      title: '邮箱',
      content: 'contact@ynuzhangwenhua.cn',
      detail: '工作时间：周一至周五 9:00-18:00'
    },
    {
      icon: ClockIcon,
      title: '工作时间',
      content: '周一至周五',
      detail: '上午 9:00-12:00，下午 14:00-18:00'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      message.error('请填写所有必填字段');
      return;
    }

    // 验证邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      message.error('请输入有效的邮箱地址');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiService.post('/contact-messages', formData);
      if (response.success) {
        message.success('消息发送成功！我们会尽快回复您');
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        message.error(response.message || '发送失败，请稍后重试');
      }
    } catch (error) {
      console.error('发送消息失败:', error);
      message.error('发送失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const directions = [
    {
      title: '公共交通',
      content: [
        '地铁1号线：大学城站下车，步行至云南大学呈贡校区',
        '地铁2号线：大学城站下车，步行至云南大学呈贡校区',
        '公交：170路、171路、172路等'
      ]
    },
    {
      title: '自驾车',
      content: [
        '导航至云南大学呈贡校区',
        '校内停车需提前申请',
        '建议使用公共交通前往'
      ]
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面标题 */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">联系我们</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          欢迎与我们联系，无论是学术交流、合作研究还是招生咨询，我们都将为您提供专业的服务。
        </p>
      </div>

      {/* 联系信息 */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">联系信息</h2>
        <div className="flex justify-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
            {contactInfo.map((info) => (
              <div key={info.title} className="card text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <info.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{info.title}</h3>
                <p className="text-gray-600 mb-1">{info.content}</p>
                <p className="text-sm text-gray-500">{info.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* 实验室位置 */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">实验室位置</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="card">
            <div className="flex items-start space-x-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-100 text-primary-600">
                <BuildingOfficeIcon className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3">实验室地址</h3>
                <div className="space-y-2 text-gray-600">
                  <p><strong>学院：</strong>材料与能源学院</p>
                  <p><strong>建筑：</strong>物理与天文学院楼</p>
                  <p><strong>楼层：</strong>负一层</p>
                  <p><strong>详细地址：</strong>云南省昆明市呈贡区云南大学呈贡校区</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card">
            <h3 className="text-xl font-bold text-gray-900 mb-4">交通指南</h3>
            <div className="space-y-4">
              {directions.map((direction) => (
                <div key={direction.title}>
                  <h4 className="font-semibold text-gray-900 mb-2">{direction.title}</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {direction.content.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 联系表单 */}
      <section>
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">发送消息</h2>
        <div className="max-w-2xl mx-auto">
          {submitted ? (
            <div className="card text-center py-12">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">消息发送成功！</h3>
              <p className="text-gray-600 mb-6">感谢您的留言，我们会尽快回复您</p>
              <button
                onClick={() => setSubmitted(false)}
                className="btn-primary px-6 py-2"
              >
                发送新消息
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  姓名 *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  邮箱 *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                主题 *
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject || ''}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">请选择主题</option>
                <option value="academic">学术交流</option>
                <option value="cooperation">合作研究</option>
                <option value="admission">招生咨询</option>
                <option value="visit">实验室参观</option>
                <option value="other">其他</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                消息内容 *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message || ''}
                onChange={handleInputChange}
                rows={6}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="请输入您的消息内容..."
              ></textarea>
            </div>
            
            <div className="text-center">
              <button
                type="submit"
                disabled={submitting}
                className={`btn-primary px-8 py-3 ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {submitting ? '发送中...' : '发送消息'}
              </button>
            </div>
          </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contact; 